import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { AuthGuard } from '../modules/auth/components/AuthGuard';
import { LoginPage } from '../modules/auth/components/LoginPage';
import { LoadingSpinner } from '../shared/components';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { PermissionGuard } from '../modules/auth/components/PermissionGuard';
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const UserManagementPage = lazy(() =>
  import('../modules/auth/components/UserManagementPage').then((m) => ({
    default: m.UserManagementPage,
  }))
);
const OrganizationPage = lazy(() =>
  import('../modules/auth/components/OrganizationPage').then((m) => ({
    default: m.OrganizationPage,
  }))
);
const CustomerListPage = lazy(() =>
  import('../modules/customer/components/CustomerListPage').then((m) => ({
    default: m.CustomerListPage,
  }))
);
const CustomerDetailPage = lazy(() =>
  import('../modules/customer/components/CustomerDetailPage').then((m) => ({
    default: m.CustomerDetailPage,
  }))
);
const ProductListPage = lazy(() =>
  import('../modules/product/components/ProductListPage').then((m) => ({
    default: m.ProductListPage,
  }))
);
const ProductDetailPage = lazy(() =>
  import('../modules/product/components/ProductDetailPage').then((m) => ({
    default: m.ProductDetailPage,
  }))
);
const QuoteListPage = lazy(() =>
  import('../modules/quote/components/QuoteListPage').then((m) => ({ default: m.QuoteListPage }))
);
const QuoteFormPage = lazy(() =>
  import('../modules/quote/components/QuoteFormPage').then((m) => ({ default: m.QuoteFormPage }))
);
const QuotePrintPage = lazy(() =>
  import('../modules/quote/components/QuotePrintPage').then((m) => ({ default: m.QuotePrintPage }))
);
const DeliveryPrintPage = lazy(() =>
  import('../modules/delivery/components/DeliveryPrintPage').then((m) => ({ default: m.DeliveryPrintPage }))
);
const InvoicePrintPage = lazy(() =>
  import('../modules/invoice/components/InvoicePrintPage').then((m) => ({ default: m.InvoicePrintPage }))
);
const PurchaseOrderPrintPage = lazy(() =>
  import('../modules/order/components/PurchaseOrderPrintPage').then((m) => ({ default: m.PurchaseOrderPrintPage }))
);
const OrderListPage = lazy(() =>
  import('../modules/order/components/OrderListPage').then((m) => ({ default: m.OrderListPage }))
);
const OrderDetailPage = lazy(() =>
  import('../modules/order/components/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage }))
);
const OrderFormPage = lazy(() =>
  import('../modules/order/components/OrderFormPage').then((m) => ({ default: m.OrderFormPage }))
);
const PurchaseOrderListPage = lazy(() =>
  import('../modules/order/components/PurchaseOrderListPage').then((m) => ({ default: m.PurchaseOrderListPage }))
);
const PurchaseOrderFormPage = lazy(() =>
  import('../modules/order/components/PurchaseOrderFormPage').then((m) => ({ default: m.PurchaseOrderFormPage }))
);
const SubcontractListPage = lazy(() =>
  import('../modules/subcontract/components/SubcontractListPage').then((m) => ({ default: m.SubcontractListPage }))
);
const SubcontractOrderFormPage = lazy(() =>
  import('../modules/subcontract/components/SubcontractOrderFormPage').then((m) => ({ default: m.SubcontractOrderFormPage }))
);
const ProcurementListPage = lazy(() =>
  import('../modules/procurement/components/ProcurementListPage').then((m) => ({ default: m.ProcurementListPage }))
);
const ProcurementOrderFormPage = lazy(() =>
  import('../modules/procurement/components/ProcurementOrderFormPage').then((m) => ({ default: m.ProcurementOrderFormPage }))
);
const TemplateListPage = lazy(() =>
  import('../modules/document').then((m) => ({ default: m.TemplateListPage }))
);
const IntegrationSettingsPage = lazy(() =>
  import('../modules/integration/components/IntegrationSettingsPage').then((m) => ({
    default: m.IntegrationSettingsPage,
  }))
);
const InvoiceListPage = lazy(() =>
  import('../modules/invoice/components/InvoiceListPage').then((m) => ({ default: m.InvoiceListPage }))
);
const PrepaymentListPage = lazy(() =>
  import('../modules/invoice/components/PrepaymentListPage').then((m) => ({ default: m.PrepaymentListPage }))
);
const PaymentReconciliationPage = lazy(() =>
  import('../modules/invoice/components/PaymentReconciliationPage').then((m) => ({ default: m.PaymentReconciliationPage }))
);
const MonthlyClosingPage = lazy(() =>
  import('../modules/invoice/components/MonthlyClosingPage').then((m) => ({ default: m.MonthlyClosingPage }))
);
const PaymentSummaryPage = lazy(() =>
  import('../modules/payment/components/PaymentSummaryPage').then((m) => ({ default: m.PaymentSummaryPage }))
);
const PaymentDetailPage = lazy(() =>
  import('../modules/payment/components/PaymentDetailPage').then((m) => ({ default: m.PaymentDetailPage }))
);
const DeliveryListPage = lazy(() =>
  import('../modules/delivery/components/DeliveryListPage').then((m) => ({ default: m.DeliveryListPage }))
);
const DeliveryFormPage = lazy(() =>
  import('../modules/delivery/components/DeliveryFormPage').then((m) => ({ default: m.DeliveryFormPage }))
);
const InvoiceFormPage = lazy(() =>
  import('../modules/invoice/components/InvoiceFormPage').then((m) => ({ default: m.InvoiceFormPage }))
);
const ReportPage = lazy(() =>
  import('../modules/report/components/ReportPage').then((m) => ({ default: m.ReportPage }))
);
const LimitTypeListPage = lazy(() =>
  import('../modules/master/components/LimitTypeListPage').then((m) => ({ default: m.LimitTypeListPage }))
);
const PaymentDateListPage = lazy(() =>
  import('../modules/master/components/PaymentDateListPage').then((m) => ({ default: m.PaymentDateListPage }))
);
const UnitListPage = lazy(() =>
  import('../modules/master/components/UnitListPage').then((m) => ({ default: m.UnitListPage }))
);
const BankListPage = lazy(() =>
  import('../modules/master/components/BankListPage').then((m) => ({ default: m.BankListPage }))
);
const BusinessTypeListPage = lazy(() =>
  import('../modules/master/components/BusinessTypeListPage').then((m) => ({ default: m.BusinessTypeListPage }))
);
const TradeTypeListPage = lazy(() =>
  import('../modules/master/components/TradeTypeListPage').then((m) => ({ default: m.TradeTypeListPage }))
);
const EmployeeCategoryListPage = lazy(() =>
  import('../modules/master/components/EmployeeCategoryListPage').then((m) => ({ default: m.EmployeeCategoryListPage }))
);
const EmployeeListPage = lazy(() =>
  import('../modules/master/components/EmployeeListPage').then((m) => ({ default: m.EmployeeListPage }))
);
const UsergroupListPage = lazy(() =>
  import('../modules/master/components/UsergroupListPage').then((m) => ({ default: m.UsergroupListPage }))
);
const CompanyProfilePage = lazy(() =>
  import('../modules/master/components/CompanyProfilePage').then((m) => ({ default: m.CompanyProfilePage }))
);

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingSpinner size="lg" fullscreen />}>
      {children}
    </Suspense>
  );
}

function ProtectedLayout() {
  return (
    <AuthGuard>
      <AppLayout />
    </AuthGuard>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPage />}
      />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<SuspenseWrapper><DashboardPage /></SuspenseWrapper>} />
        <Route path="/customers" element={<SuspenseWrapper><CustomerListPage /></SuspenseWrapper>} />
        <Route path="/customers/:id" element={<SuspenseWrapper><CustomerDetailPage /></SuspenseWrapper>} />
        <Route path="/products" element={<SuspenseWrapper><ProductListPage /></SuspenseWrapper>} />
        <Route path="/products/:id" element={<SuspenseWrapper><ProductDetailPage /></SuspenseWrapper>} />
        <Route path="/quotes" element={<SuspenseWrapper><QuoteListPage /></SuspenseWrapper>} />
        <Route path="/quotes/new" element={<SuspenseWrapper><QuoteFormPage /></SuspenseWrapper>} />
        <Route path="/quotes/:id/edit" element={<SuspenseWrapper><QuoteFormPage /></SuspenseWrapper>} />
        <Route path="/quotes/:id/print" element={<SuspenseWrapper><QuotePrintPage /></SuspenseWrapper>} />
        <Route path="/orders" element={<SuspenseWrapper><OrderListPage /></SuspenseWrapper>} />
        <Route path="/orders/new" element={<SuspenseWrapper><OrderFormPage /></SuspenseWrapper>} />
        <Route path="/orders/:id/edit" element={<SuspenseWrapper><OrderFormPage /></SuspenseWrapper>} />
        <Route path="/orders/:id" element={<SuspenseWrapper><OrderDetailPage /></SuspenseWrapper>} />
        <Route path="/purchase-orders" element={<SuspenseWrapper><PurchaseOrderListPage /></SuspenseWrapper>} />
        <Route path="/purchase-orders/new" element={<SuspenseWrapper><PurchaseOrderFormPage /></SuspenseWrapper>} />
        <Route path="/purchase-orders/:id/edit" element={<SuspenseWrapper><PurchaseOrderFormPage /></SuspenseWrapper>} />
        <Route path="/purchase-orders/:id/print" element={<SuspenseWrapper><PurchaseOrderPrintPage /></SuspenseWrapper>} />
        <Route path="/subcontracts" element={<SuspenseWrapper><SubcontractListPage /></SuspenseWrapper>} />
        <Route path="/subcontracts/new" element={<SuspenseWrapper><SubcontractOrderFormPage /></SuspenseWrapper>} />
        <Route path="/subcontracts/:id/edit" element={<SuspenseWrapper><SubcontractOrderFormPage /></SuspenseWrapper>} />
        <Route path="/subcontracts/:id" element={<Navigate to="/subcontracts" replace />} />
        <Route path="/procurement" element={<SuspenseWrapper><ProcurementListPage /></SuspenseWrapper>} />
        <Route path="/procurement/new" element={<SuspenseWrapper><ProcurementOrderFormPage /></SuspenseWrapper>} />
        <Route path="/procurement/:id/edit" element={<SuspenseWrapper><ProcurementOrderFormPage /></SuspenseWrapper>} />
        <Route path="/procurement/:id" element={<Navigate to="/procurement" replace />} />
        <Route path="/deliveries" element={<SuspenseWrapper><DeliveryListPage /></SuspenseWrapper>} />
        <Route path="/deliveries/new" element={<SuspenseWrapper><DeliveryFormPage /></SuspenseWrapper>} />
        <Route path="/deliveries/:id/edit" element={<SuspenseWrapper><DeliveryFormPage /></SuspenseWrapper>} />
        <Route path="/deliveries/:id/print" element={<SuspenseWrapper><DeliveryPrintPage /></SuspenseWrapper>} />
        <Route path="/deliveries/:id" element={<SuspenseWrapper><DeliveryListPage /></SuspenseWrapper>} />
        <Route path="/invoices" element={<SuspenseWrapper><InvoiceListPage /></SuspenseWrapper>} />
        <Route path="/invoices/new" element={<SuspenseWrapper><InvoiceFormPage /></SuspenseWrapper>} />
        <Route path="/invoices/:id/edit" element={<SuspenseWrapper><InvoiceFormPage /></SuspenseWrapper>} />
        <Route path="/invoices/:id/print" element={<SuspenseWrapper><InvoicePrintPage /></SuspenseWrapper>} />
        <Route path="/invoices/prepayment" element={<SuspenseWrapper><PrepaymentListPage /></SuspenseWrapper>} />
        <Route path="/payments" element={<SuspenseWrapper><PaymentReconciliationPage /></SuspenseWrapper>} />
        <Route path="/payable" element={<SuspenseWrapper><PaymentSummaryPage /></SuspenseWrapper>} />
        <Route path="/payable/detail" element={<SuspenseWrapper><PaymentDetailPage /></SuspenseWrapper>} />
        <Route
          path="/closings"
          element={
            <SuspenseWrapper>
              <PermissionGuard resource="monthly_closings" action="read" fallback={<PlaceholderPage title="権限がありません" />}>
                <MonthlyClosingPage />
              </PermissionGuard>
            </SuspenseWrapper>
          }
        />
        <Route path="/documents" element={<SuspenseWrapper><TemplateListPage /></SuspenseWrapper>} />
        <Route path="/reports" element={<SuspenseWrapper><ReportPage /></SuspenseWrapper>} />
        <Route path="/settings" element={<PlaceholderPage title="設定" />} />
        <Route
          path="/settings/users"
          element={
            <SuspenseWrapper>
              <PermissionGuard resource="users" action="read" fallback={<PlaceholderPage title="権限がありません" />}>
                <UserManagementPage />
              </PermissionGuard>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/settings/organization"
          element={
            <SuspenseWrapper>
              <PermissionGuard resource="departments" action="read" fallback={<PlaceholderPage title="権限がありません" />}>
                <OrganizationPage />
              </PermissionGuard>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/settings/integrations"
          element={
            <SuspenseWrapper>
              <PermissionGuard resource="users" action="read" fallback={<PlaceholderPage title="権限がありません" />}>
                <IntegrationSettingsPage />
              </PermissionGuard>
            </SuspenseWrapper>
          }
        />
        <Route path="/master/limit-types" element={<SuspenseWrapper><PermissionGuard resource="limitTypes" action="read" fallback={<PlaceholderPage title="権限がありません" />}><LimitTypeListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/payment-dates" element={<SuspenseWrapper><PermissionGuard resource="paymentDates" action="read" fallback={<PlaceholderPage title="権限がありません" />}><PaymentDateListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/units" element={<SuspenseWrapper><PermissionGuard resource="units" action="read" fallback={<PlaceholderPage title="権限がありません" />}><UnitListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/banks" element={<SuspenseWrapper><PermissionGuard resource="banks" action="read" fallback={<PlaceholderPage title="権限がありません" />}><BankListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/business-types" element={<SuspenseWrapper><PermissionGuard resource="businessTypes" action="read" fallback={<PlaceholderPage title="権限がありません" />}><BusinessTypeListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/trade-types" element={<SuspenseWrapper><PermissionGuard resource="tradeTypes" action="read" fallback={<PlaceholderPage title="権限がありません" />}><TradeTypeListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/employee-categories" element={<SuspenseWrapper><PermissionGuard resource="employeeCategories" action="read" fallback={<PlaceholderPage title="権限がありません" />}><EmployeeCategoryListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/employees" element={<SuspenseWrapper><PermissionGuard resource="employees" action="read" fallback={<PlaceholderPage title="権限がありません" />}><EmployeeListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/usergroups" element={<SuspenseWrapper><PermissionGuard resource="usergroups" action="read" fallback={<PlaceholderPage title="権限がありません" />}><UsergroupListPage /></PermissionGuard></SuspenseWrapper>} />
        <Route path="/master/company-profile" element={<SuspenseWrapper><PermissionGuard resource="companyProfile" action="read" fallback={<PlaceholderPage title="権限がありません" />}><CompanyProfilePage /></PermissionGuard></SuspenseWrapper>} />
      </Route>
      <Route path="*" element={<SuspenseWrapper><NotFoundPage /></SuspenseWrapper>} />
    </Routes>
  );
}
